import type { Store } from '@warp-drive/core';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import {
  EMPLOYEES_PATH,
  PARTICIPATION_BUCKETS,
  companyEmployeeClause,
} from 'tio-employee/utils/people-facets';
import type { DashboardContext } from 'tio-employee/utils/partner-dashboard/registry';

/** "Added recently" covers employees created in this many trailing days. */
export const RECENTLY_ADDED_DAYS = 7;

/** Employee counts at the company, as the employees page buckets them. */
export interface Headcount {
  /** Active employees who have registered. */
  participating: number;
  /** Active employees who were invited but haven't registered. */
  pending: number;
  /** Employee records created in the last `RECENTLY_ADDED_DAYS` days, active or not. */
  recentlyAdded: number;
}

/**
 * One size-0 Person search counting the company's employee entries. It reuses
 * the employees page's participation buckets, so the numbers match its facets.
 */
export async function loadHeadcount(ctx: DashboardContext): Promise<Headcount> {
  if (!ctx.companyId) return { participating: 0, pending: 0, recentlyAdded: 0 };
  const builder = new OpenSearchQueryBuilder().setPagination(1, 0);
  // The top-level term satisfies SearchPolicy, which requires a company filter.
  builder.mustTerm('company_ids', ctx.companyId);
  builder.addNestedFilteredNamedFiltersAggregation(
    'headcount',
    EMPLOYEES_PATH,
    [companyEmployeeClause(ctx.companyId)],
    {
      participating: PARTICIPATION_BUCKETS.participating,
      pending: PARTICIPATION_BUCKETS.pending,
      added: {
        range: { 'employees_nested.created_at_date': { gte: `now-${RECENTLY_ADDED_DAYS}d` } },
      },
    }
  );
  const store = ctx.owner.lookup('service:store') as Store;
  const { content } = await store.request<OpenSearchResponse>(
    executeSearchRequest({ 'model-type': 'Person', 'search-body': builder.build() })
  );
  const counts =
    OpenSearchResponseBuilder.from(content).getNestedFilteredNamedFiltersAggregation('headcount');
  return {
    participating: counts['participating'] ?? 0,
    pending: counts['pending'] ?? 0,
    recentlyAdded: counts['added'] ?? 0,
  };
}
